import { useCallback, useEffect, useState } from "react";
import { Check, Download, RefreshCw } from "lucide-react";
import { formatDate, STATUS_LABELS, STATUS_BADGE, downloadCsv } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPovinneRuceni = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [notes, setNotes] = useState({});
  const [savedNote, setSavedNote] = useState("");

  const load = useCallback(async (statusFilter) => {
    setLoading(true);
    setListError("");
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : "";
      const res = await fetch(`${API}/admin/povinne-ruceni${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const data = await res.json();
      setItems(data.items || []);
      setStatuses(data.statuses || []);
      setNotes(Object.fromEntries((data.items || []).map((i) => [i.id, i.internal_note || ""])));
    } catch {
      setListError("Poptávky se nepodařilo načíst. Zkontrolujte připojení.");
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const changeStatus = async (id, status) => {
    const prev = items;
    setItems(items.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      const res = await fetch(`${API}/admin/povinne-ruceni/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (res.status === 401 || res.status === 403) logout();
      else if (!res.ok) setItems(prev);
    } catch {
      setItems(prev);
    }
  };

  const saveNote = async (id) => {
    try {
      const res = await fetch(`${API}/admin/povinne-ruceni/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ internal_note: notes[id] || "" }),
      });
      if (res.status === 401 || res.status === 403) return logout();
      if (res.ok) {
        setSavedNote(id);
        setTimeout(() => setSavedNote(""), 2000);
      }
    } catch {
      /* tiché selhání — poznámku lze uložit znovu */
    }
  };

  const exportCsv = () =>
    downloadCsv(
      `${API}/admin/povinne-ruceni-export`,
      "povinne-ruceni-poptavky.csv",
      token,
      logout,
      () => setListError("Export se nepodařilo stáhnout.")
    );

  return (
    <div data-testid="admin-tab-povinne-ruceni">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-2xl md:text-3xl text-[#252525]">Hlídání povinného ručení</h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => load(filter)}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 border border-[#E5E5E5] bg-white hover:border-[#B69A58] transition-colors duration-300 px-5 py-3 text-xs uppercase tracking-widest font-medium text-[#252525]"
          >
            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
            Obnovit
          </button>
          <button
            type="button"
            onClick={exportCsv}
            data-testid="admin-export-csv-button"
            className="inline-flex items-center gap-2 border border-[#E5E5E5] bg-white hover:border-[#B69A58] transition-colors duration-300 px-5 py-3 text-xs uppercase tracking-widest font-medium text-[#252525]"
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <label htmlFor="admin-filter" className="text-xs uppercase tracking-widest text-[#737373] font-medium">
          Filtrovat podle stavu
        </label>
        <select
          id="admin-filter"
          data-testid="admin-status-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-white border border-[#E5E5E5] focus:border-[#B69A58] outline-none py-2 px-3 text-sm rounded-none"
        >
          <option value="">Všechny stavy</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
          ))}
        </select>
        <p className="text-sm text-[#737373]" data-testid="admin-count">
          {loading ? "Načítám…" : `${items.length} ${items.length === 1 ? "poptávka" : items.length >= 2 && items.length <= 4 ? "poptávky" : "poptávek"}`}
        </p>
      </div>

      {listError && <p role="alert" data-testid="admin-list-error" className="text-sm text-red-700 mt-6">{listError}</p>}

      {!loading && items.length === 0 && !listError && (
        <div className="mt-8 border border-[#F0F0F0] bg-white p-10 text-center" data-testid="admin-empty-list">
          <p className="text-base text-[#737373]">Zatím žádné poptávky{filter ? " ve zvoleném stavu" : ""}.</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-8 overflow-x-auto border border-[#F0F0F0] bg-white">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="border-b border-[#F0F0F0] text-left">
                <th className="px-5 py-4 text-xs uppercase tracking-widest text-[#737373] font-medium">Přijato</th>
                <th className="px-5 py-4 text-xs uppercase tracking-widest text-[#737373] font-medium">Klient</th>
                <th className="px-5 py-4 text-xs uppercase tracking-widest text-[#737373] font-medium">Další období od</th>
                <th className="px-5 py-4 text-xs uppercase tracking-widest text-[#737373] font-medium">Kontaktovat</th>
                <th className="px-5 py-4 text-xs uppercase tracking-widest text-[#737373] font-medium">Pojišťovna</th>
                <th className="px-5 py-4 text-xs uppercase tracking-widest text-[#737373] font-medium">Stav</th>
                <th className="px-5 py-4 text-xs uppercase tracking-widest text-[#737373] font-medium">Interní poznámka</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-testid={`admin-inquiry-row-${item.id}`} className="border-b border-[#F0F0F0] align-top">
                  <td className="px-5 py-4 whitespace-nowrap text-[#737373]">{formatDate(item.created_at)}</td>
                  <td className="px-5 py-4">
                    <p className="font-medium text-[#252525]">{item.name}</p>
                    <p className="text-[#4A4A4A] mt-1">{item.email}</p>
                    {item.phone && <p className="text-[#737373] mt-1">{item.phone}</p>}
                    {item.note && <p className="text-[#737373] mt-2 italic max-w-[220px]">„{item.note}"</p>}
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap text-[#252525] font-medium">{formatDate(item.period_start)}</td>
                  <td className="px-5 py-4 whitespace-nowrap">
                    <p className="text-[#252525]">{formatDate(item.planned_contact)}</p>
                    {item.priority && (
                      <span className="inline-block mt-2 text-xs font-medium bg-[#FAF6EC] text-[#8A6D2F] px-2 py-1" data-testid={`admin-priority-${item.id}`}>
                        PRIORITA — méně než 70 dní
                      </span>
                    )}
                    {item.late_notice && (
                      <span className="inline-block mt-2 text-xs font-medium bg-[#FAF6EC] text-[#8A6D2F] px-2 py-1" data-testid={`admin-late-${item.id}`}>
                        Ověřit výpovědní lhůtu
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-[#4A4A4A]">{item.insurer || "—"}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block text-xs font-medium px-2 py-1 mb-2 ${STATUS_BADGE[item.status] || "bg-[#F5F5F5] text-[#4A4A4A]"}`}>
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                    <select
                      data-testid={`admin-status-select-${item.id}`}
                      value={item.status}
                      onChange={(e) => changeStatus(item.id, e.target.value)}
                      className="block bg-white border border-[#E5E5E5] focus:border-[#B69A58] outline-none py-2 px-2 text-sm rounded-none w-full"
                      aria-label={`Změnit stav poptávky ${item.name}`}
                    >
                      {statuses.map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4">
                    <textarea
                      data-testid={`admin-note-input-${item.id}`}
                      rows={2}
                      value={notes[item.id] ?? ""}
                      onChange={(e) => setNotes({ ...notes, [item.id]: e.target.value })}
                      className="bg-white border border-[#E5E5E5] focus:border-[#B69A58] outline-none py-2 px-3 w-56 text-sm rounded-none resize-y"
                    />
                    <button
                      type="button"
                      onClick={() => saveNote(item.id)}
                      data-testid={`admin-note-save-${item.id}`}
                      className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#252525] hover:text-[#B69A58] transition-colors duration-300"
                    >
                      {savedNote === item.id ? (
                        <>
                          <Check className="h-4 w-4 text-[#B69A58]" strokeWidth={1.5} />
                          Uloženo
                        </>
                      ) : (
                        "Uložit poznámku"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
